import React from "react";

export function PageHeader({ label, title, subtitle, action }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
      <div className="min-w-0">
        <div className="kr-label" data-testid="page-label">{label}</div>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight mt-1 break-words">
          {title}
        </h1>
        {subtitle && <p className="text-xs sm:text-sm text-zinc-500 mt-1 max-w-2xl">{subtitle}</p>}
      </div>
      {action && <div className="w-full sm:w-auto flex flex-wrap gap-2">{action}</div>}
    </div>
  );
}

export function Card({ children, className = "", ...rest }) {
  return (
    <div className={`kr-card p-4 sm:p-6 ${className}`} {...rest}>{children}</div>
  );
}

export function StatTile({ label, value, sub, accent, testId }) {
  return (
    <div className="kr-card kr-tile p-4 sm:p-5" data-testid={testId}>
      <div className="kr-label">{label}</div>
      <div className={`font-display font-black text-2xl sm:text-3xl lg:text-4xl mt-2 tracking-tight ${accent || "text-zinc-900"}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-zinc-500 mt-1.5">{sub}</div>}
    </div>
  );
}

export function Field({ label, children, full = false }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="block kr-label mb-1.5">{label}</label>
      {children}
    </div>
  );
}

export function Input(props) {
  return (
    <input
      {...props}
      className={
        "w-full px-3 py-2 border border-zinc-300 bg-white text-sm focus:outline-none focus:border-orange-600 focus:ring-1 focus:ring-orange-600 " +
        (props.className || "")
      }
    />
  );
}

export function Select(props) {
  return (
    <select
      {...props}
      className={
        "w-full px-3 py-2 border border-zinc-300 bg-white text-sm focus:outline-none focus:border-orange-600 focus:ring-1 focus:ring-orange-600 " +
        (props.className || "")
      }
    >
      {props.children}
    </select>
  );
}

export function Btn({ variant = "primary", children, ...rest }) {
  const base = "px-4 py-2 text-xs uppercase tracking-wider font-bold inline-flex items-center justify-center gap-2 transition-colors";
  const styles = {
    primary: "kr-btn-primary",
    secondary: "border border-zinc-900 bg-white text-zinc-900 hover:bg-zinc-100",
    ghost: "text-zinc-600 hover:text-zinc-900",
    danger: "bg-red-600 text-white hover:bg-red-700 border border-red-700",
  };
  return (
    <button {...rest} className={`${base} ${styles[variant]} ${rest.className || ""}`}>
      {children}
    </button>
  );
}

export function Badge({ tone = "zinc", children }) {
  const map = {
    zinc: "bg-zinc-100 text-zinc-700 border-zinc-200",
    orange: "bg-orange-50 text-orange-700 border-orange-200",
    green: "bg-green-50 text-green-700 border-green-200",
    red: "bg-red-50 text-red-700 border-red-200",
    yellow: "bg-yellow-50 text-yellow-700 border-yellow-200",
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${map[tone]}`}>
      {children}
    </span>
  );
}

export function Modal({ open, onClose, title, children, size = "md" }) {
  if (!open) return null;
  const w = size === "lg" ? "sm:max-w-4xl" : size === "xl" ? "sm:max-w-6xl" : "sm:max-w-lg";
  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/60 flex items-stretch sm:items-center justify-center sm:p-4 overflow-y-auto" data-testid="modal-overlay">
      <div className={`bg-white border-0 sm:border sm:border-zinc-300 w-full ${w} min-h-full sm:min-h-0 sm:max-h-[90vh] sm:rounded-none flex flex-col overflow-hidden`}>
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 sm:p-5 border-b border-zinc-200 bg-white">
          <h3 className="font-display text-lg sm:text-xl font-black uppercase tracking-tight truncate pr-2">{title}</h3>
          <button data-testid="modal-close" onClick={onClose} className="text-zinc-500 hover:text-zinc-900 text-2xl leading-none w-8 h-8 flex items-center justify-center rounded-full hover:bg-zinc-100 transition-colors shrink-0">×</button>
        </div>
        <div className="p-4 sm:p-5 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
}

export function EmptyState({ title, hint }) {
  return (
    <div className="text-center py-16 border border-dashed border-zinc-300 bg-white">
      <div className="kr-label">Empty</div>
      <div className="font-display text-xl font-bold mt-1">{title}</div>
      {hint && <p className="text-sm text-zinc-500 mt-2">{hint}</p>}
    </div>
  );
}
